import { CbhCustomChatMessage } from "@src/appV2/Chat/CbhCustomChatMessage";
import { ChatChannel } from "../chat.types";
import { useSession } from "@src/appV2/Auth/api/useSession";
import {
  transformResponseIntoPlacementApplication,
  canFetchPlacementApplications,
  useFetchPaginatedPlacementApplications,
} from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { getPlacementChatContextFromChannel } from "../utils/placementChat";
import { InterviewStatus } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { formatDateTime } from "@clipboard-health/date-time";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";

interface PlacementApplicationMessageProps {
  currentChannel: ChatChannel;
  channelUrl: string;
}

export function PlacementApplicationInterviewCancelled({
  currentChannel,
  channelUrl,
}: PlacementApplicationMessageProps) {
  const { profile, user } = useSession();
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();
  const { workplaceId, workerId, placementId } = getPlacementChatContextFromChannel(channelUrl);

  const { data: placementApplicationsData } = useFetchPaginatedPlacementApplications(
    {
      workplaceId: profile.userId,
      queryParams: {
        filter: {
          "placement.id": placementId,
          "worker.id": workerId,
          "workplace.id": workplaceId,
        },
      },
    },
    {
      enabled:
        isDefined(profile) &&
        isPlacementsEnabled &&
        isDefined(placementId) &&
        canFetchPlacementApplications(user?.access ?? []),
    }
  );

  const placementApplicationPage = placementApplicationsData?.pages.at(0);
  const placementApplicationData = placementApplicationPage?.data.at(0);
  const { hcpName: workerName } = currentChannel.metadata ?? {};
  if (
    isNil(placementApplicationPage) ||
    isNil(placementApplicationPage.data) ||
    isNil(placementApplicationPage.included) ||
    isNil(placementApplicationData) ||
    isNil(workerName)
  ) {
    return undefined as unknown as React.ReactElement;
  }

  const placementApplication = transformResponseIntoPlacementApplication(
    placementApplicationData,
    placementApplicationPage.included
  );

  const cancelledInterviews = placementApplication.interviews.filter(
    (interview) => interview.status === InterviewStatus.CANCELLED
  );

  if (cancelledInterviews.length === 0) {
    return undefined as unknown as React.ReactElement;
  }

  const workerQualification = placementApplication?.placement.workerTypes?.[0]
    ? placementApplication?.placement.workerTypes?.[0]
    : "";

  const cancelledInterviewDateTime = formatDateTime(cancelledInterviews[0].start, {
    timeZone: profile.tmz,
  });

  /* Note: Currently, we can't tie an interview to the chat message.
    So if there is more than one cancelled interview (which is an edge case), we don't know whether the workplace canceled them. 
    We only know that if the workplace received this message, they cancelled an interview at some point. 
    That's why we show "interview" in the singular form if the length is greater than one. We also hide the interview times. */
  const message =
    cancelledInterviews.length === 1
      ? `${workerName}'s ${workerQualification} interview on ${cancelledInterviewDateTime} has been cancelled by the workplace.`
      : `${workerName}'s ${workerQualification} interview has been cancelled by the workplace.`;

  return (
    <CbhCustomChatMessage
      message={message}
      avatarSxProps={{ justifyContent: "flex-start" }}
      messageContainerSxProps={{ width: "50%" }}
    ></CbhCustomChatMessage>
  );
}
